import {
  effect,
  EnvironmentInjector,
  inject,
  InjectionToken,
  makeEnvironmentProviders,
  runInInjectionContext,
  Service,
  untracked,
  type EnvironmentProviders,
} from '@angular/core';
import { TokenStore } from './token-store.service';

/**
 * Callbacks registered via {@link provideAuthenticatedInitializer}. They run once each time
 * authentication is established — i.e. on a successful sign-in AND when a valid session already
 * exists on app load (direct navigation / refresh to an authenticated URL). This is the analogue
 * of Angular's `provideAppInitializer`, but gated on authentication instead of app bootstrap.
 */
export const AUTHENTICATED_INITIALIZER = new InjectionToken<ReadonlyArray<() => void>>(
  'AUTHENTICATED_INITIALIZER',
);

/**
 * Register a callback to run once whenever the user becomes authenticated (each sign-in, and once
 * at load when a session is already valid). Mirrors `provideAppInitializer`; the callback runs in
 * an injection context, so it may use `inject()`.
 *
 * @example
 * provideAuthenticatedInitializer(() => inject(CurrentAccountService).load());
 */
export function provideAuthenticatedInitializer(callback: () => void): EnvironmentProviders {
  return makeEnvironmentProviders([
    { provide: AUTHENTICATED_INITIALIZER, useValue: callback, multi: true },
  ]);
}

/**
 * Fires the registered {@link AUTHENTICATED_INITIALIZER} callbacks on each `false → true` edge of
 * `TokenStore.authenticated()`, and re-arms on sign-out so the next sign-in runs them again. The
 * `authenticated` signal hydrates synchronously from localStorage, so a refresh / deep-link into
 * an authenticated URL also triggers them once. Bootstrapped by `provideAuthentication()`.
 */
@Service()
export class AuthenticatedInitializerRunner {
  private readonly tokenStore = inject(TokenStore);
  private readonly injector = inject(EnvironmentInjector);
  private readonly initializers = inject(AUTHENTICATED_INITIALIZER, { optional: true }) ?? [];

  constructor() {
    let armed = true;

    effect(() => {
      if (this.tokenStore.authenticated()) {
        if (armed) {
          armed = false;
          // Outside the reactive context, so a callback may create its own effect (`toObservable`).
          untracked(() =>
            this.initializers.forEach((callback) => runInInjectionContext(this.injector, callback)),
          );
        }
      } else {
        armed = true;
      }
    });
  }
}
